import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import moment from "moment";

export const formatDueDate = (date) => {
  const due = dayjs(date);
  const now = dayjs();

  if (due.isSame(now, "day")) {
    return "Today · " + due.format("HH:mm A");
  }

  if (due.isSame(now.add(1, "day"), "day")) {
    return "Tomorrow · " + due.format("HH:mm A");
  }

  if (due.isSame(now.subtract(1, "day"), "day")) {
    return "Yesterday · " + due.format("HH:mm A");
  }

  // If within 24 hours show relative time
  if (Math.abs(due.diff(now, "hour")) < 24) {
    return due.fromNow(); // e.g. "in 3 hours"
  }

  // Otherwise full date
  return due.format("dddd, MMM D, YYYY HH:mm A");
};

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export function formatSmartDate(date) {
  const d = dayjs(date);
  const now = dayjs();

  const diffMinutes = now.diff(d, "minute");
  const diffHours = now.diff(d, "hour");

  // Few minutes ago (0–5 mins)
  if (diffMinutes < 5) {
    return "Few minutes ago";
  }

  // Minutes ago (< 60 mins)
  if (diffMinutes < 60) {
    return `${diffMinutes} minutes ago`;
  }

  // Today → hours ago
  if (d.isToday()) {
    return `${diffHours} hours ago`;
  }

  // Yesterday
  if (d.isYesterday()) {
    return `Yesterday at ${d.format("hh:mm A")}`;
  }

  // Default full date
  return d.format("DD MMMM YYYY, hh:mm A");
}

export function formatTime(date) {
  if (!date) return "";
  const past = moment.utc(date).local();
  const now = moment();

  const diffMinutes = now.diff(past, "minutes");
  const diffHours = now.diff(past, "hours");
  const diffDays = now.diff(past, "days");

  if (diffMinutes < 1) {
    return "Just now";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
  }

  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  }

  if (diffDays === 1) {
    return "Yesterday";
  }

  return past.format("MMM D, YY");
}

export function formatWhatsAppChatTime(dateInput) {
  if (!dateInput) return "";
  const d = dayjs(dateInput);
  if (!d.isValid()) return "";

  const now = dayjs();
  const todayStart = now.startOf("day");
  const yesterdayStart = todayStart.subtract(1, "day");
  const sixDaysAgoStart = todayStart.subtract(6, "day");

  if (d.isAfter(todayStart) || d.isSame(todayStart, "day")) {
    return d.format("hh:mm A");
  } else if (d.isAfter(yesterdayStart) || d.isSame(yesterdayStart, "day")) {
    return "Yesterday";
  } else if (d.isAfter(sixDaysAgoStart) || d.isSame(sixDaysAgoStart, "day")) {
    return d.format("dddd");
  } else {
    return d.format("DD/MM/YYYY");
  }
}

export function getChatWindowDateLabel(dateInput) {
  if (!dateInput) return "";
  const d = dayjs(dateInput);
  if (!d.isValid()) return "";

  const now = dayjs();
  const todayStart = now.startOf("day");
  const yesterdayStart = todayStart.subtract(1, "day");
  const sixDaysAgoStart = todayStart.subtract(6, "day");

  if (d.isAfter(todayStart) || d.isSame(todayStart, "day")) {
    return "Today";
  } else if (d.isAfter(yesterdayStart) || d.isSame(yesterdayStart, "day")) {
    return "Yesterday";
  } else if (d.isAfter(sixDaysAgoStart) || d.isSame(sixDaysAgoStart, "day")) {
    return d.format("dddd");
  } else {
    return d.format("MMMM D, YYYY");
  }
}

export function formatDateTime(dateInput, format = "MMM DD, YYYY, hh:mm A") {
  if (!dateInput) return "-";
  const m = moment(dateInput);
  if (!m.isValid()) return dateInput;
  return m.format(format);
}
